import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  VideoLibrary as RecordingIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Schedule as ClockIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";

const formatDuration = (s) => {
  if (!s) return null;
  const m = Math.round(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

/**
 * Recordings of the student's own group's classes.
 *
 * Playback URLs are signed and short-lived, so one is fetched per view rather than embedded in
 * the list — a recording of a classroom must not sit behind a shareable permanent link.
 */
export default function ClassRecordings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("student/class-recordings");
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load recordings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const play = async (row) => {
    setOpening(row.id);
    try {
      const res = await api.get(`student/class-recordings/${row.id}/playback`);
      const url = res.data?.data?.url;
      if (!url) throw new Error("no url");
      setPlaying({ ...row, url });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not open this recording.");
    } finally {
      setOpening(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <RecordingIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Class Recordings
          </Typography>
        </Stack>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Catch up on your group's live classes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          {[1, 2].map((n) => (
            <Skeleton key={n} variant="rounded" height={110} />
          ))}
        </Stack>
      ) : rows.length === 0 && !error ? (
        <Alert severity="info">No recordings are available yet.</Alert>
      ) : (
        <Stack spacing={2}>
          {rows.map((r) => (
            <Card key={r.id} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "center" }}
                  spacing={2}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                      {r.group?.name && <Chip size="small" variant="outlined" label={r.group.name} />}
                      {formatDuration(r.duration_seconds) && (
                        <Chip size="small" variant="outlined" label={formatDuration(r.duration_seconds)} />
                      )}
                    </Stack>
                    <Typography variant="h6" fontWeight={700}>
                      {r.class?.name || r.title}
                    </Typography>
                    {r.recorded_at && (
                      <Typography variant="caption" color="text.secondary">
                        Recorded {new Date(r.recorded_at).toLocaleDateString()}
                      </Typography>
                    )}
                    {r.available_until && (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                        <ClockIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                        <Typography variant="caption" color="text.secondary">
                          Available until {new Date(r.available_until).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<PlayIcon />}
                    onClick={() => play(r)}
                    disabled={opening === r.id}
                    sx={{ flexShrink: 0 }}
                  >
                    {opening === r.id ? "Opening…" : "Watch"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(playing)} onClose={() => setPlaying(null)} maxWidth="md" fullWidth>
        <DialogTitle>{playing?.class?.name || playing?.title}</DialogTitle>
        <DialogContent dividers>
          {playing?.url && (
            // Cloudflare Stream HLS. The signed URL expires, so this is fetched per view.
            <video
              controls
              autoPlay
              style={{ width: "100%", borderRadius: 8, background: "#000" }}
              src={playing.url}
            >
              <track kind="captions" />
            </video>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setPlaying(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
